import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import { Ghost, RefreshCw } from "lucide-react";

/**
 * Rejected Trades Shadow — counterfactual P&L for signals that a portfolio-
 * level risk gate rejected. Answers the user's question: "if I had taken
 * this trade anyway, what would I have made?"
 *
 * Two views:
 *   - Summary tiles: total would-have-made, win rate, best/worst.
 *   - Full ledger: sortable table of every rejected shadow trade.
 */
export default function ShadowRejected() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(30);
  const [underlying, setUnderlying] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { days };
      if (underlying) params.underlying = underlying;
      const { data } = await api.get("/shadow-rejected", { params });
      setData(data);
    } catch (e) {
      toast.error("Failed to load shadow P&L");
    } finally {
      setBusy(false);
    }
  }, [days, underlying]);

  const runBackfill = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/shadow-rejected/backfill?days=${days}`);
      toast.success(`Backfilled ${data.computed}/${data.scanned} signals`);
      await load();
    } catch (e) {
      toast.error("Backfill failed");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const summary = useMemo(() => data?.summary || {}, [data]);
  const byUnderlying = useMemo(
    () => Object.entries(summary.by_underlying || {}),
    [summary],
  );
  const byCode = useMemo(
    () => Object.entries(summary.by_rejection_code || {}),
    [summary],
  );

  return (
    <div className="space-y-6" data-testid="shadow-rejected-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <Ghost size={20} className="text-amber-400" />
            <h1 className="text-2xl tracking-tight text-slate-100">
              Rejected Trades — Shadow P&amp;L
            </h1>
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl uppercase tracking-[0.15em]">
            Counterfactual P&amp;L for signals rejected by portfolio risk gates.
            Simulates entry → SL / target / 15:10 exit at real charges.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            data-testid="shadow-days-select"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-slate-900 border border-slate-700 rounded-sm px-2 py-1 text-xs text-slate-200"
          >
            <option value={7}>Last 7d</option>
            <option value={30}>Last 30d</option>
            <option value={90}>Last 90d</option>
            <option value={365}>Last 1y</option>
          </select>
          <select
            data-testid="shadow-underlying-select"
            value={underlying}
            onChange={(e) => setUnderlying(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded-sm px-2 py-1 text-xs text-slate-200"
          >
            <option value="">All</option>
            <option value="NIFTY">NIFTY</option>
            <option value="BANKNIFTY">BANKNIFTY</option>
            <option value="SENSEX">SENSEX</option>
          </select>
          <button
            data-testid="shadow-refresh-btn"
            onClick={load}
            disabled={busy}
            className="px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-[0.15em] border border-slate-700 hover:bg-slate-800/50 flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            data-testid="shadow-backfill-btn"
            onClick={runBackfill}
            disabled={busy}
            className="px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-[0.15em] border border-blue-500 text-blue-300 hover:bg-blue-500/10 disabled:opacity-50"
          >
            Backfill
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile
          label="Would have made"
          value={fmtMoney(summary.would_have_made || 0)}
          tone={summary.would_have_made > 0 ? "pos" : summary.would_have_made < 0 ? "neg" : "neutral"}
          testid="shadow-total"
        />
        <Tile
          label="Rejected trades"
          value={summary.count || 0}
          testid="shadow-count"
        />
        <Tile
          label="Win rate"
          value={`${((summary.win_rate || 0) * 100).toFixed(1)}%`}
          testid="shadow-winrate"
        />
        <Tile
          label={summary.wins ? "W / L" : "no data yet"}
          value={summary.count ? `${summary.wins} / ${summary.losses}` : "—"}
          testid="shadow-wl"
        />
      </div>

      {/* By-underlying + by-code breakdown */}
      {(byUnderlying.length > 0 || byCode.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          <BreakdownCard title="By underlying" rows={byUnderlying} testid="shadow-by-underlying" />
          <BreakdownCard title="By rejection code" rows={byCode} testid="shadow-by-code" />
        </div>
      )}

      {/* Best / worst callouts */}
      {(summary.best || summary.worst) && (
        <div className="grid md:grid-cols-2 gap-4">
          <BestWorstCard title="Best missed" row={summary.best} tone="pos" testid="shadow-best" />
          <BestWorstCard title="Worst avoided" row={summary.worst} tone="neg" testid="shadow-worst" />
        </div>
      )}

      {/* Full ledger */}
      <div className="surface rounded-sm overflow-hidden" data-testid="shadow-ledger">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
            Ledger — {rows.length} rejections
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 bg-slate-900/40">
              <tr>
                <th className="text-left px-3 py-2">Candle</th>
                <th className="text-left px-3 py-2">Underlying</th>
                <th className="text-left px-3 py-2">Contract</th>
                <th className="text-left px-3 py-2">Rejection</th>
                <th className="text-right px-3 py-2">Entry</th>
                <th className="text-right px-3 py-2">Exit</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-right px-3 py-2">Gross</th>
                <th className="text-right px-3 py-2">Charges</th>
                <th className="text-right px-3 py-2">Net P&amp;L</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-8 text-slate-500 text-xs">
                    No risk-limit rejections found in this window. Try backfilling.
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const netPos = (r.net_pnl || 0) > 0;
                return (
                  <tr key={r.signal_id} className="border-t border-slate-800/50 hover:bg-slate-900/40">
                    <td className="px-3 py-2 font-mono text-slate-400">{r.candle_timestamp}</td>
                    <td className="px-3 py-2 text-slate-200">{r.underlying}</td>
                    <td className="px-3 py-2 font-mono text-slate-300">{r.tradingsymbol}</td>
                    <td className="px-3 py-2 text-slate-400 text-[10px]">
                      {r.rejection_code}
                      {r.rejection_reason && (
                        <span className="block text-slate-500 italic">{r.rejection_reason}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{r.entry_price?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.exit_price?.toFixed(2)}</td>
                    <td className={`px-3 py-2 text-[10px] uppercase tracking-[0.1em] ${
                      r.exit_reason === "target" ? "text-pos" :
                      r.exit_reason === "stop" ? "text-neg" : "text-slate-400"
                    }`}>{r.exit_reason}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-400">{fmtMoney(r.gross_pnl)}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">{fmtMoney(r.total_charges)}</td>
                    <td className={`px-3 py-2 text-right font-mono font-semibold ${netPos ? "text-pos" : "text-neg"}`}>
                      {fmtMoney(r.net_pnl)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-[10px] text-slate-500 leading-relaxed max-w-3xl">
        <strong>How this is computed:</strong> option price is projected forward
        from the underlying's post-signal OHLC using a first-order delta
        approximation (Δ×ΔS). Exit is the first of: stop-loss hit, target hit,
        or forced 15:10 IST exit. Charges include brokerage + STT + GST +
        exchange fees (both legs). Numbers are counterfactual, not actual —
        real fills can differ due to slippage & spread widening under stress.
      </div>
    </div>
  );
}

function Tile({ label, value, tone = "neutral", testid }) {
  const cls = tone === "pos" ? "text-pos" : tone === "neg" ? "text-neg" : "text-slate-100";
  return (
    <div className="surface rounded-sm p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`text-2xl font-mono mt-2 ${cls}`}>{value}</div>
    </div>
  );
}

function BreakdownCard({ title, rows, testid }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="surface rounded-sm p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">{title}</div>
      <div className="space-y-1.5 text-xs font-mono">
        {rows.map(([k, v]) => {
          const pos = (v.net_pnl || 0) > 0;
          return (
            <div key={k} className="flex justify-between gap-3">
              <span className="text-slate-300 truncate">{k}</span>
              <span className="text-slate-500 shrink-0">
                {v.count}× · <span className={pos ? "text-pos" : "text-neg"}>{fmtMoney(v.net_pnl)}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BestWorstCard({ title, row, tone, testid }) {
  if (!row) return null;
  return (
    <div className="surface rounded-sm p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{title}</div>
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-sm text-slate-200 font-mono">{row.tradingsymbol}</div>
          <div className="text-[10px] text-slate-500 mt-1">
            {row.candle_timestamp} · {row.exit_reason}
          </div>
        </div>
        <div className={`text-xl font-mono ${tone === "pos" ? "text-pos" : "text-neg"}`}>
          {fmtMoney(row.net_pnl)}
        </div>
      </div>
    </div>
  );
}
